import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { ISizeFormValues } from '../../../../../@types/model/masterData/size/sizeFormValues';
import { useAppSelector } from '../../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect, IOptionType, MuiAutocompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import SizeModelHelper from '../../../../../@types/model/masterData/size/sizeModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import { ICommodity } from '../../../../../@types/model/masterData/commodity/commodity';
import lodash from 'lodash';
import OptionType from '../../../../../@types/model/optionType';
import CustomReactHookFormDevTools from '../../../../../components/devtools/ReactHookFormDevTools';

interface ISizeFormProps {
    isLoading : boolean;
    selectedCommodity : ICommodity | null;
    initialValues : ISizeFormValues;
    onSubmit : (values : ISizeFormValues) => void;
    onClose : () => void;
}

const SizeForm = (props : ISizeFormProps) : React.ReactElement => {
    const packs = useAppSelector(x => x.masterData.packs);
    const commodities = useAppSelector(x => x.masterData.commodities);

    const [selectedCommodity, setSelectedCommodity] = useState<IOptionType | null>(null);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<ISizeFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(SizeModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        if (!lodash.isEqual(props.selectedCommodity, selectedCommodity)) {
            setSelectedCommodity(
                props.selectedCommodity
                    ? OptionType.fromDataModel(props.selectedCommodity)
                    : null
            );
        }
    }, [props.selectedCommodity]);

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onCommodityChange = (selectedOption : IOptionType | null) : void => {
        setSelectedCommodity(selectedOption);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const commodityOptions = useMemo(() => {
        return commodities
            .filter(x => x.isActive && (!props.selectedCommodity || x.id === props.selectedCommodity.id))
            .map(OptionType.fromDataModel);
    }, [commodities, props.selectedCommodity]);

    const packOptions = useMemo(() => {
        return packs
            .filter(x => x.isActive && (!selectedCommodity || x.commodityId === selectedCommodity.value))
            .map(OptionType.fromDataModel);
    }, [packs, selectedCommodity]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <div className={'fdr wfill'}>
                        <ControlledTextField
                            className={'mb10 wfill'}
                            control={control}
                            label={'Code'}
                            name={'code'}
                            defaultValue={props.initialValues.code}
                            size={'small'}
                        />
                        <div className={'w20'}/>
                        <ControlledTextField
                            className={'mb10 wfill'}
                            control={control}
                            label={'Name'}
                            name={'name'}
                            defaultValue={props.initialValues.name}
                            size={'small'}
                        />
                    </div>
                    <MuiAutocompleteSelect
                        label={'Commodity'}
                        name={'commodity'}
                        className={'mb10'}
                        value={selectedCommodity}
                        options={commodityOptions}
                        onChange={(value) => onCommodityChange(value as IOptionType | null)}
                        placeholder={'Use to filter down packs'}
                        disabled={!!props.selectedCommodity}
                    />
                    <ControlledMuiAutoCompleteSelect
                        className={'mb10'}
                        control={control}
                        label={'Packs'}
                        defaultValues={props.initialValues.packs}
                        options={packOptions}
                        isMulti
                        name={'packs'}
                        // tagLimit={5}
                    />
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox 
                            name={'isActive'}
                            label={'Is Active?'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button 
                            className={'cpd mr20'}
                            onClick={props.onClose} 
                            type={'reset'}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default SizeForm;
